import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { Newspaper, RefreshCw } from "lucide-react";

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000 / 86400);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function ArticleCard({ article }) {
  const [expanded, setExpanded] = useState(false);
  const preview = article.snippet?.slice(0, 220);
  const isLong = article.snippet?.length > 220;

  return (
    <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px 24px", transition: "all 0.2s" }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(220,38,38,0.4)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}>

      <span style={{ fontSize: 8, fontWeight: 800, color: "#dc2626", background: "rgba(220,38,38,0.1)", padding: "2px 7px", borderRadius: 100, textTransform: "uppercase", letterSpacing: "0.06em", display: "inline-block", marginBottom: 12 }}>Binos Special</span>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, background: article.color || "#dc2626", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
          {article.avatar}
        </div>
        <div>
          <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0 }}>{article.author}</p>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0 }}>{article.fund} · {timeAgo(article.created_at)}</p>
        </div>
      </div>

      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.7, margin: 0, fontStyle: "italic" }}>
        "{expanded ? article.snippet : preview}
        {isLong && (
          <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", color: "#dc2626", fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 0 0 4px", fontFamily: "inherit" }}>
            {expanded ? " show less" : "... read more"}
          </button>
        )}"
      </p>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px 24px" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ width: 60, height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4 }} />
        <div style={{ width: 40, height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4 }} />
      </div>
      <div style={{ height: 14, background: "rgba(255,255,255,0.06)", borderRadius: 4, marginBottom: 8, width: "85%" }} />
      <div style={{ height: 11, background: "rgba(255,255,255,0.04)", borderRadius: 4, marginBottom: 6, width: "95%" }} />
      <div style={{ height: 11, background: "rgba(255,255,255,0.04)", borderRadius: 4, width: "70%" }} />
    </div>
  );
}

export default function NewsFeed() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchArticles = async () => {
    setLoading(true);
    setError("");
    const { data, error } = await supabase
      .from("binos_specials")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      setError("Could not load articles. Please try again.");
    } else {
      setArticles(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchArticles(); }, []);

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, paddingBottom: 20, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: "0 0 3px", letterSpacing: "-0.5px" }}>Insights Feed</h2>
          <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.15em", margin: 0 }}>Curated by Binos</p>
        </div>
        <button onClick={fetchArticles} disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", cursor: "pointer", fontFamily: "inherit", opacity: loading ? 0.5 : 1 }}>
          <RefreshCw style={{ width: 11, height: 11 }} /> Refresh
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: "14px 18px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#dc2626" }}>{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1,2,3].map(i => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Articles */}
      {!loading && articles.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {articles.map(article => <ArticleCard key={article.id} article={article} />)}
        </div>
      )}

      {/* Empty state */}
      {!loading && articles.length === 0 && !error && (
        <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 18, padding: "80px 48px", textAlign: "center" }}>
          <div style={{ width: 52, height: 52, background: "rgba(255,255,255,0.04)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <Newspaper style={{ width: 22, height: 22, color: "rgba(255,255,255,0.15)" }} />
          </div>
          <p style={{ fontSize: 17, fontWeight: 900, color: "rgba(255,255,255,0.2)", letterSpacing: "-0.5px", margin: "0 0 6px" }}>No articles yet</p>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.15)", fontWeight: 500, margin: 0 }}>Check back soon for curated insights from leading VCs</p>
        </div>
      )}
    </div>
  );
}
